import { debounce } from 'lodash';
import { useEffect, useRef } from 'react';
import { FieldValues, Path, UseFormWatch } from 'react-hook-form';

/**
 * Auto-submits form on field value changes with optional debounce.
 *
 * IMPORTANT: Form element must have `onSubmit={(e) => e.preventDefault()}`
 * to prevent page reload on Enter key press.
 * TODO: Find better solution for Enter key handling.
 *
 * Callers pass an inline `handleSubmit(...)` and an inline field array, so both change every
 * render - the effect must not depend on them or a re-render cancels the pending debounce.
 */
export const useAutoSubmitForm = <TFieldValues extends FieldValues>(
  handleFormSubmit: () => void,
  watch: UseFormWatch<TFieldValues>,
  debouncedFields: Path<TFieldValues>[] = [],
) => {
  const submitRef = useRef(handleFormSubmit);
  submitRef.current = handleFormSubmit;

  const fieldsKey = debouncedFields.join('|');

  useEffect(() => {
    const fields = fieldsKey ? fieldsKey.split('|') : [];
    const debouncedSubmit = debounce(() => submitRef.current(), 800);

    const subscription = watch((value, { name }) => {
      // when name undefined it means that form is reset need ran handleFormSubmit
      const isDebouncedField = name ? fields.includes(name) : false;

      if (isDebouncedField) {
        debouncedSubmit();
      } else {
        submitRef.current();
      }
    });

    return () => {
      subscription.unsubscribe();
      debouncedSubmit.cancel();
    };
  }, [watch, fieldsKey]);
};
